/**
 * Quanto de receita cada escola privada representa para a EGBA.
 *
 * ## O que é dado e o que é estimativa
 *
 * A distinção percorre este arquivo inteiro, porque confundir as duas coisas é
 * como uma planilha de mercado vira uma meta que ninguém bate:
 *
 * | grandeza | origem | natureza |
 * |---|---|---|
 * | matrículas na 3ª série | Censo Escolar/INEP | **dado publicado** |
 * | concluintes | matrículas × taxa de aprovação | estimativa |
 * | centímetros de coluna | nº de nomes × métrica de composição | estimativa |
 * | valor | cm/coluna × tabela vigente | **cálculo exato sobre a estimativa** |
 *
 * ## O que NÃO existe em dado público, e por isso não está aqui
 *
 * **Faturamento da escola.** Não é público. O que existe é porte (ME/EPP/demais)
 * e capital social na base da Receita — ambos proxies fracos: capital social de
 * R$ 10.000 convive com mil alunos. Estão no cadastro como contexto, e não
 * entram em nenhuma conta.
 *
 * **Índice acadêmico de escola privada.** O IDEB depende do SAEB, cuja adesão
 * da rede privada é baixa e não censitária; e o INEP parou de publicar ENEM por
 * escola em 2015. Prometer "índice acadêmico" para as 3.320 privadas da Bahia
 * seria inventar número. Quando a escola tem IDEB, ele aparece; quando não tem,
 * o campo fica vazio e a tela diz por quê.
 *
 * ## Por que a métrica de composição é parâmetro, e não constante
 *
 * Quantos centímetros ocupam 300 nomes depende do corpo da fonte, da entrelinha
 * e de quantas colunas a matéria usa — decisões da oficina, não da lei. Os
 * padrões abaixo saem da composição real do Caderno Diversos, e a EGBA
 * recalibra pelo painel a partir dos próprios pedidos já publicados, sem deploy.
 */

/** Como a oficina compõe uma relação de nomes. Tudo em centímetros. */
export interface MetricaComposicao {
  /** Altura de uma linha de nome no corpo usado no Caderno Diversos. */
  alturaLinhaCm: number
  /** Colunas em que a relação é diagramada. */
  colunas: number
  /** Cabeçalho, texto de abertura e assinatura — não dependem do nº de nomes. */
  alturaFixaCm: number
  /**
   * Nomes que cabem numa linha da coluna.
   *
   * Normalmente 1. Escolas com nomes curtos e coluna larga chegam a 2, e é a
   * diferença entre cobrar por 300 linhas ou por 150.
   */
  nomesPorLinha: number
  /**
   * Taxa de aprovação da 3ª série na rede privada.
   *
   * Matrícula não é conclusão. A rede privada da Bahia fica na casa dos 95 %,
   * e o número é parâmetro para acompanhar a série do INEP sem alterar código.
   */
  taxaConclusao: number
  /**
   * Fração das matrículas de curso técnico que concluem no ano.
   *
   * As colunas do médio trazem a **série** (`_3` é o ano de formatura), mas as
   * do técnico concomitante e subsequente trazem a matrícula **total do curso**,
   * somando todos os períodos. Tratá-las como concluintes conta um curso de
   * dois anos duas vezes — foi o que fez o SESI Retiro aparecer com 4.754
   * formandos num único ano.
   *
   * 0,5 corresponde à duração típica de dois anos. É parâmetro porque a duração
   * varia por eixo tecnológico, e a EGBA calibra pelos pedidos reais.
   */
  fracaoConclusaoTecnico: number
}

export const METRICA_PADRAO: MetricaComposicao = {
  alturaLinhaCm: 0.35,
  colunas: 3,
  alturaFixaCm: 2,
  nomesPorLinha: 1,
  taxaConclusao: 0.95,
  fracaoConclusaoTecnico: 0.5,
}

/** O que o Censo Escolar diz sobre a escola, já somado por etapa. */
export interface MatriculasEscola {
  /** 3ª série do médio propedêutico. */
  medio3: number
  /** 3ª série do médio integrado a curso técnico. */
  medioTecnico3: number
  /** 3ª série do normal/magistério. */
  medioNormal3: number
  /** Técnico concomitante e subsequente — concluem e também publicam. */
  tecnicoConcomitante: number
  tecnicoSubsequente: number
  /** EJA de nível médio. */
  ejaMedio: number
}

export const MATRICULAS_ZERO: MatriculasEscola = {
  medio3: 0,
  medioTecnico3: 0,
  medioNormal3: 0,
  tecnicoConcomitante: 0,
  tecnicoSubsequente: 0,
  ejaMedio: 0,
}

/**
 * Matrículas que desembocam numa publicação de concluintes.
 *
 * EJA de nível médio entra: a Resolução CEE 44/2020 fala de concluintes do
 * Ensino Médio, sem excluir a modalidade. Técnico concomitante e subsequente
 * também — são cursos que emitem certificado próprio.
 *
 * O técnico **integrado** já está em `medioTecnico3`; somá-lo de novo pela
 * coluna do técnico contaria o mesmo aluno duas vezes.
 *
 * Este número é a matrícula bruta e serve para conferência com o Censo. Quem
 * estima formandos é `potencialDaEscola`, que aplica taxa diferente ao médio
 * (série de formatura) e ao técnico (matrícula do curso inteiro).
 */
export function matriculasElegiveis(m: MatriculasEscola): number {
  return (
    n(m.medio3) +
    n(m.medioTecnico3) +
    n(m.medioNormal3) +
    n(m.tecnicoConcomitante) +
    n(m.tecnicoSubsequente) +
    n(m.ejaMedio)
  )
}

/** Só as etapas cuja coluna do Censo já é a série de formatura. */
function matriculasDeFormatura(m: MatriculasEscola): number {
  return n(m.medio3) + n(m.medioTecnico3) + n(m.medioNormal3) + n(m.ejaMedio)
}

/** Técnico concomitante e subsequente: matrícula do curso inteiro. */
function matriculasDeCursoTecnico(m: MatriculasEscola): number {
  return n(m.tecnicoConcomitante) + n(m.tecnicoSubsequente)
}

function n(v: number | null | undefined): number {
  return Number.isFinite(v) && (v as number) > 0 ? Math.trunc(v as number) : 0
}

export interface PotencialEscola {
  /** Soma das matrículas que podem virar publicação. Dado do Censo. */
  matriculasElegiveis: number
  /** Estimativa: matrículas × taxa de conclusão. */
  concluintesEstimados: number
  /** Estimativa: centímetros de coluna da matéria. */
  cmColunaEstimado: number
  /** Cálculo exato sobre a estimativa acima. */
  valorEstimadoCentavos: number
}

/**
 * Potencial de uma escola.
 *
 * `valorCmColunaCentavos` vem da tabela de preços vigente, e não de constante
 * daqui: o dia em que a EGBA reajustar, o mercado inteiro se recalcula sozinho.
 */
export function potencialDaEscola(
  matriculas: MatriculasEscola,
  valorCmColunaCentavos: number,
  metrica: MetricaComposicao = METRICA_PADRAO,
): PotencialEscola {
  const elegiveis = matriculasElegiveis(matriculas)
  const concluintes =
    Math.round(matriculasDeFormatura(matriculas) * limitar(metrica.taxaConclusao, 0, 1)) +
    Math.round(
      matriculasDeCursoTecnico(matriculas) * limitar(metrica.fracaoConclusaoTecnico, 0, 1),
    )

  if (concluintes === 0) {
    return {
      matriculasElegiveis: elegiveis,
      concluintesEstimados: 0,
      cmColunaEstimado: 0,
      valorEstimadoCentavos: 0,
    }
  }

  const nomesPorLinha = Math.max(1, Math.trunc(metrica.nomesPorLinha))
  const linhas = Math.ceil(concluintes / nomesPorLinha)
  const colunas = Math.max(1, Math.trunc(metrica.colunas))

  // A altura é a da coluna mais alta: a relação é distribuída entre as colunas,
  // e é a altura da matéria — não a soma das colunas — que multiplica o preço.
  const alturaCm = Math.ceil(linhas / colunas) * Math.max(0, metrica.alturaLinhaCm)
  const alturaTotal = alturaCm + Math.max(0, metrica.alturaFixaCm)

  // A tabela cobra por centímetro de coluna: altura × nº de colunas.
  const cmColuna = Math.ceil(alturaTotal * colunas)

  return {
    matriculasElegiveis: elegiveis,
    concluintesEstimados: concluintes,
    cmColunaEstimado: cmColuna,
    valorEstimadoCentavos: cmColuna * Math.max(0, Math.trunc(valorCmColunaCentavos)),
  }
}

function limitar(v: number, min: number, max: number): number {
  if (!Number.isFinite(v)) return min
  return Math.min(max, Math.max(min, v))
}

// ---------------------------------------------------------------------------
// Situação da escola no funil
// ---------------------------------------------------------------------------

export const SITUACOES_FUNIL = [
  /** Já publicou pela plataforma. */
  'cliente',
  /** Tem pedido em andamento, ainda não publicado. */
  'em_negociacao',
  /** Preencheu formulário ou entrou em campanha, sem pedido. */
  'lead',
  /** Está no mercado e nunca foi tocada. */
  'nao_trabalhada',
] as const

export type SituacaoFunil = (typeof SITUACOES_FUNIL)[number]

export const ROTULOS_FUNIL: Record<SituacaoFunil, { nome: string; descricao: string }> = {
  cliente: { nome: 'Cliente', descricao: 'Já publicou pela plataforma.' },
  em_negociacao: { nome: 'Em negociação', descricao: 'Tem pedido aberto, ainda não publicado.' },
  lead: { nome: 'Lead', descricao: 'Demonstrou interesse ou está em campanha.' },
  nao_trabalhada: { nome: 'Não trabalhada', descricao: 'No mercado e ainda sem contato.' },
}

// ---------------------------------------------------------------------------
// Agregação de mercado
// ---------------------------------------------------------------------------

export interface LinhaMercado {
  situacao: SituacaoFunil
  concluintesEstimados: number
  valorEstimadoCentavos: number
}

export interface ResumoMercado {
  escolas: number
  concluintes: number
  valorCentavos: number
  porSituacao: Record<SituacaoFunil, { escolas: number; concluintes: number; valorCentavos: number }>
  /**
   * Quantas escolas concentram 80 % do valor.
   *
   * O número que decide como a EGBA aborda o mercado: se 80 % da receita está
   * em 60 escolas, a operação é uma lista de ligações; se está em 900, é
   * campanha. Ninguém consegue essa resposta olhando uma tabela ordenada.
   */
  escolasPara80Pct: number
}

export function resumirMercado(linhas: readonly LinhaMercado[]): ResumoMercado {
  const vazio = () => ({ escolas: 0, concluintes: 0, valorCentavos: 0 })
  const porSituacao = {
    cliente: vazio(),
    em_negociacao: vazio(),
    lead: vazio(),
    nao_trabalhada: vazio(),
  } as ResumoMercado['porSituacao']

  let valorTotal = 0
  let concluintes = 0
  for (const l of linhas) {
    const balde = porSituacao[l.situacao] ?? porSituacao.nao_trabalhada
    balde.escolas += 1
    balde.concluintes += l.concluintesEstimados
    balde.valorCentavos += l.valorEstimadoCentavos
    valorTotal += l.valorEstimadoCentavos
    concluintes += l.concluintesEstimados
  }

  const ordenado = [...linhas].sort((a, b) => b.valorEstimadoCentavos - a.valorEstimadoCentavos)
  const alvo = valorTotal * 0.8
  let acumulado = 0
  let escolasPara80Pct = 0
  for (const l of ordenado) {
    if (acumulado >= alvo) break
    acumulado += l.valorEstimadoCentavos
    escolasPara80Pct += 1
  }

  return {
    escolas: linhas.length,
    concluintes,
    valorCentavos: valorTotal,
    porSituacao,
    escolasPara80Pct,
  }
}
